import { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Zap, Shield, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { track, trackIds } from "@/lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentsModal({ open, onOpenChange, lang, theme, defaultEmail }) {
  const dark = theme === "dark";
  const ar = lang === "ar";
  const [packages, setPackages] = useState([]);
  const [testMode, setTestMode] = useState(false);
  const [email, setEmail] = useState(defaultEmail || "");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/payments/packages`).then((r) => {
      const list = r.data.packages || [];
      setPackages(list);
      setSelected((prev) => (list.some((p) => p.id === prev) ? prev : (list.find((p) => p.id === "sub_monthly") || list[0])?.id || null));
      setTestMode(!!r.data.test_mode);
    });
  }, [open]);

  useEffect(() => {
    setEmail(defaultEmail || "");
  }, [defaultEmail]);

  const pay = async () => {
    if (!email || !email.includes("@")) {
      toast.error(ar ? "أدخل بريدًا صحيحًا" : "Enter a valid email");
      return;
    }
    if (!selected) return;
    setLoading(true);
    try {
      localStorage.setItem("pl_email", email.trim().toLowerCase());
      const { data } = await axios.post(`${API}/payments/checkout`, {
        package_id: selected,
        email: email.trim().toLowerCase(),
        origin_url: window.location.origin,
        ...trackIds(),
      });
      // Open Tap checkout in a new window when running inside an iframe embed.
      const inIframe = typeof window !== "undefined" && window.self !== window.top;
      if (inIframe) {
        const w = window.open(data.checkout_url, "_blank", "noopener,noreferrer");
        if (!w) window.location.href = data.checkout_url;
      } else {
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      toast.error(ar ? "تعذّر بدء الدفع" : "Failed to start payment");
      setLoading(false);
    }
  };

  const dc = dark ? "bg-[#1f2a47] border-[#2e3a5c] text-white" : "bg-white text-[#2d2c35]";
  const cardBase = dark ? "bg-[#1f2a47] border-[#2e3a5c]" : "bg-white border-[#e5e5ea]";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="payments-modal" className={`max-w-2xl max-h-[90vh] overflow-y-auto ${dc}`} dir={ar ? "rtl" : "ltr"}>
        <DialogHeader>
          <DialogTitle className={`text-xl font-bold flex items-center gap-2 ${ar ? "font-arabic" : "font-outfit"}`}>
            <Zap className="h-5 w-5 text-[#18c98f]" />
            {ar ? "شراء اقتراحات إضافية" : "Buy more AI suggestions"}
          </DialogTitle>
          <DialogDescription className={`${dark ? "text-white/70" : "text-[#2d2c35]/70"} ${ar ? "font-arabic" : "font-outfit"}`}>
            {ar
              ? "تحصل على ٣ حسابات مجانية شهريًا، وتحميل عرض السعر PDF مجاني دائمًا. الشراء لحسابات الذكاء الاصطناعي الإضافية فقط. دفع آمن عبر Tap (مدى، فيزا، ماستركارد، Apple Pay)."
              : "You get 3 free calculations a month and PDF proposals are always free. Purchases only add extra AI calculations. Secure payment via Tap (Mada, Visa, Mastercard, Apple Pay)."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {testMode && (
            <div
              data-testid="test-mode-banner"
              className={`rounded-xl p-3 border ${dark ? "bg-yellow-950/40 border-yellow-500/40 text-yellow-100" : "bg-yellow-50 border-yellow-300 text-yellow-900"}`}
            >
              <div className={`text-sm font-bold mb-1 ${ar ? "font-arabic" : "font-outfit"}`}>
                {ar ? "🧪 وضع الاختبار (Test Mode)" : "🧪 Test Mode"}
              </div>
              <div className={`text-xs leading-relaxed ${ar ? "font-arabic" : "font-outfit"}`}>
                {ar ? "البطاقات الحقيقية لن تعمل. استخدم بطاقة اختبار:" : "Real cards will not work. Use a test card:"}
              </div>
              <div className="mt-1.5 text-xs font-mono-num" dir="ltr">
                <div><span className="opacity-70">Card:</span> <b>5123 4500 0000 0008</b></div>
                <div><span className="opacity-70">Expiry:</span> <b>12/28</b> · <span className="opacity-70">CVV:</span> <b>100</b> · <span className="opacity-70">OTP:</span> <b>12345</b></div>
              </div>
            </div>
          )}

          <div>
            <Label className={dark ? "text-white" : "text-[#2d2c35]"}>
              {ar ? "البريد الإلكتروني" : "Email"}
            </Label>
            <Input
              data-testid="payments-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className={`h-11 mt-2 ${dark ? "bg-[#1f2a47] border-[#2e3a5c] text-white" : "bg-white border-[#e5e5ea]"}`}
              dir="ltr"
            />
            <p className={`mt-1 text-[11px] ${dark ? "text-white/60" : "text-[#2d2c35]/60"}`}>
              {ar ? "نستخدم البريد لربط رصيدك أو اشتراكك بحسابك وإرسال الإيصال." : "We link your credits or subscription to this email and send your receipt."}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {packages.map((p) => {
              const active = selected === p.id;
              const isSub = p.type === "subscription";
              const bestValue = p.id === "pack_100" || p.id === "sub_yearly";
              const popular = p.id === "sub_monthly";
              const perUnit = isSub ? null : (p.amount / p.credits).toFixed(2);
              const subSuffix = p.id === "sub_yearly"
                ? (ar ? "/سنة · لامحدود" : "/year · unlimited")
                : (ar ? "/شهر · لامحدود" : "/month · unlimited");
              return (
                <button
                  key={p.id}
                  data-testid={`pkg-${p.id}`}
                  onClick={() => { if (selected !== p.id) track("package_select", { package: p.id }, { once: false }); setSelected(p.id); }}
                  className={`relative rounded-2xl p-4 border-2 text-start transition ${
                    active
                      ? "bg-[#18c98f]/15 border-[#18c98f] shadow-[0_8px_24px_-8px_rgba(24,201,143,0.4)]"
                      : isSub
                      ? `${cardBase} border-[#18c98f]/40 hover:border-[#18c98f]`
                      : `${cardBase} hover:border-[#18c98f]/60`
                  }`}
                >
                  {bestValue && (
                    <span className="absolute -top-2 end-3 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#18c98f] text-black">
                      {ar ? "الأوفر" : "Best value"}
                    </span>
                  )}
                  {popular && (
                    <span className="absolute -top-2 end-3 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#18c98f] text-black">
                      {ar ? "الأكثر شيوعًا" : "Most popular"}
                    </span>
                  )}
                  <div className={`text-lg font-bold ${dark ? "text-white" : "text-[#2d2c35]"}`}>
                    {ar ? p.name_ar : p.name_en}
                  </div>
                  <div className="flex items-baseline gap-1 mt-1">
                    <span className={`text-3xl font-bold font-mono-num ${dark ? "text-[#18c98f]" : "text-[#12a777]"}`}>
                      {p.amount}
                    </span>
                    <span className={`text-sm ${dark ? "text-white/70" : "text-[#2d2c35]/70"}`}>
                      {ar ? "ريال" : "SAR"}
                    </span>
                  </div>
                  <div className={`text-[11px] mt-1 ${dark ? "text-white/60" : "text-[#2d2c35]/60"}`}>
                    {isSub ? subSuffix : (ar ? `~${perUnit} ريال/اقتراح` : `~${perUnit} SAR each`)}
                  </div>
                  <ul data-testid={`pkg-terms-${p.id}`} className={`mt-2 space-y-0.5 text-[10.5px] leading-snug ${dark ? "text-white/75" : "text-[#2d2c35]/75"} ${ar ? "font-arabic" : "font-outfit"}`}>
                    <li>• {ar ? p.usage_ar : p.usage_en}</li>
                    <li>• {ar ? p.validity_ar : p.validity_en}</li>
                    <li>• {ar ? "دفعة واحدة — بدون تجديد تلقائي" : "One-time payment — no auto-renewal"}</li>
                  </ul>
                </button>
              );
            })}
          </div>

          <div className={`flex items-center gap-2 text-[11px] ${dark ? "text-white/60" : "text-[#2d2c35]/60"} ${ar ? "font-arabic" : "font-outfit"}`}>
            <Shield className="h-3.5 w-3.5 text-[#18c98f]" />
            {ar ? "دفع آمن ومشفّر — نحن لا نحفظ بيانات بطاقتك" : "Encrypted secure payment — we never store your card details"}
          </div>

          <Button
            data-testid="checkout-button"
            onClick={pay}
            disabled={loading || !selected || !email}
            className="w-full h-12 bg-gradient-to-r from-[#18c98f] to-[#12a777] hover:from-[#12a777] hover:to-[#0e8a62] text-[#1f2a47] font-bold rounded-full gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {loading
              ? (ar ? "جارٍ التحويل إلى بوابة الدفع..." : "Redirecting to secure checkout...")
              : (ar ? "المتابعة إلى الدفع" : "Continue to secure checkout")}
            {!loading && <ArrowRight className={`h-4 w-4 ${ar ? "rotate-180" : ""}`} />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
